import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { cacheService, orderService } from "../../../Core/Api/services";
import TableSkeleton from "../../../Components/Table/TableSkeleton";
import OrderItemPanel from "../../../Components/Panels/OrderItemPanel/order-item-panel";
import { useLocation, useNavigate } from "react-router";
import { DateRange } from "@mui/lab";
import dayjs, { Dayjs } from "dayjs";
import { DbDeliveryWatermarkNexguardInfos } from "../../../Core/Api/Types/db-delivery-watermark-nexguard-infos";
import DataTable, { CellSize } from "../../../Components/Table/table";
import { GridRowParams } from "@mui/x-data-grid";
import TableCellTypography, {
    TableCellLongTypography,
} from "../../../Components/Table/TableCellTypography/table-cell-typography";
import TableCellDateTime from "../../../Components/Table/TableCellDateTime/table-cell-date-time";
import ListPageHeader, {
    ListPageHeaderGroupActions,
    ListPageHeaderRight,
} from "../../../Components/Misc/ListPageHeader";
import { DateRangePicker, SingleInputDateRangeField, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    IconButton,
    Link,
    OutlinedInput,
    Stack,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmptyListSlot from "../../../Components/Misc/EmptyListSlot";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import { AclMatch, UserHasAcl, UserHasAcls } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import { routePaths } from "../../../Router/router";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import useEventListener from "../../../Utils/Hooks/useEventListener";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { PickersShortcutsItem } from "@mui/x-date-pickers";
import TableUuids from "../../../Components/Table/table-uuids";

export const shortcutsItems: PickersShortcutsItem<any>[] = [
    {
        label: "Last Year",
        getValue: () => {
            const today = dayjs();
            return [today.add(-1, "year"), today];
        },
    },
    {
        label: "Last 3 Months",
        getValue: () => {
            const today = dayjs();
            return [today.add(-3, "months"), today];
        },
    },

    {
        label: "30 Last Days",
        getValue: () => {
            const today = dayjs();
            return [today.add(-30, "day"), today];
        },
    },
    {
        label: "Last 7 Days",
        getValue: () => {
            const today = dayjs();
            return [today.add(-7, "day"), today];
        },
    },
    {
        label: "Today",
        getValue: () => {
            const today = dayjs();
            return [today.startOf("day"), today.endOf("day")];
        },
    },
    {
        label: "This Week",
        getValue: () => {
            const today = dayjs();
            return [today.startOf("week"), today.endOf("week")];
        },
    },
    {
        label: "Next Week",
        getValue: () => {
            const today = dayjs();
            const nextWeek = today.add(7, "day");
            return [nextWeek.startOf("week"), nextWeek.endOf("week")];
        },
    },
    {
        label: "Next Month",
        getValue: () => {
            const today = dayjs();
            const startOfNextMonth = today.endOf("month").add(1, "day");
            return [startOfNextMonth, startOfNextMonth.endOf("month")];
        },
    },

    { label: "Reset", getValue: () => [null, null] },
];

export const SingleLabelValue: React.FC<{
    label: string | null | undefined | number;
    value: string | null | undefined | number;
    link?: string | null | undefined;
}> = ({ label, value, link }) => {
    return (
        <Stack display={"flex"} direction={"row"} width={"100%"} justifyContent={"space-between"} alignItems={"center"}>
            <Typography flex={1} color={"text.tier"}>
                {label}
            </Typography>
            {link ? (
                <Link href={link} target={"_blank"} align="right" underline="hover">
                    {value}
                </Link>
            ) : (
                <Typography color={value ? "text.primary" : "text.secondary"} align="right" flex={2}>
                    {value ?? "null"}
                </Typography>
            )}
        </Stack>
    );
};

enum SearchState {
    waitingInput = "waitingInput",
    loading = "loading",
    result = "result",
    noResult = "noResult",
    error = "error",
}

function OrderWatermarks() {
    const [openPanelOrderItem, setOpenPanelOrderItem] = React.useState(false);
    const [selectedOrderItemId, setSelectedOrderItemId] = React.useState<number>(-1);
    const [watermarks, setWatermarks] = React.useState<DbDeliveryWatermarkNexguardInfos[]>([]);
    const [lastWatermarks, setLastWatermarks] = React.useState<DbDeliveryWatermarkNexguardInfos[]>([]);
    const [watermark, setWatermark] = React.useState<DbDeliveryWatermarkNexguardInfos | undefined>();
    const [searchByKey, setSearchByKey] = React.useState<number | undefined>(0);
    const [lastN, setLastN] = useState(20);
    const [activeTab, setActiveTab] = useState(0);
    const [searchState, setSearchState] = useState<SearchState>(SearchState.waitingInput);
    const [errors, setErrors] = useState<string[]>(["", "", ""]);
    const [filterResults, setFilterResults] = useState("");
    const [firstLoads, setFirstLoads] = useState(-1);
    const didSearchByTab = useRef<boolean[]>([false, false, false]);

    const today = dayjs();
    const location = useLocation();
    const navigate = useNavigate();

    const cachedFilters = cacheService.GetPageFilter(location.pathname) ?? {};

    const [dateValue, setDateValue] = React.useState<DateRange<Dayjs>>(
        cachedFilters["date"] && cachedFilters["date"][0]
            ? cachedFilters["date"].map((x) => dayjs(x))
            : [today.subtract(1, "months"), today]
    );
    const [dateChange, setDateChange] = useState(true);

    useEffect(() => {
        if (!UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclAdmin], AclMatch.OneAtLeast)) navigate(-1);
    }, []);

    useEffect(() => {
        if (firstLoads < 1)
            switch (activeTab) {
                case 0:
                    if (firstLoads === 0) return;
                    fetchLastN(lastN, lastWatermarks);
                    setFirstLoads(0);
                    break;
                case 1:
                    fetchRange(dateValue);
                    setFirstLoads(1);
                    break;
                default:
                    break;
            }
    }, [activeTab, lastN, searchByKey, dateValue, lastWatermarks.length, watermark?.id, firstLoads]);

    const hidePanelOrder = () => {
        setOpenPanelOrderItem(false);
    };
    const showPanelOrder = (e: any) => {
        setSelectedOrderItemId(e);
        setOpenPanelOrderItem(true);
    };

    const columns = useMemo(() => {
        const result = [
            {
                field: "id",
                headerName: "Payload / Id",
                flex: CellSize.xxsmall,
                minWidth: CellSize.xxsmall,
                renderCell: (data: any) => <TableCellTypography color={"text.primary"} fontWeight={700} data={data.value} />,
            },

            {
                field: "orderItemId",
                headerName: "Order Item Id",
                flex: CellSize.xxsmall,
                minWidth: CellSize.xxsmall,
                renderCell: (data: any) => <TableCellTypography color={"text.secondary"} fontWeight={500} data={data.value} />,
            },
            {
                field: "date",
                headerName: "Date",
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
                renderCell: (data: any) => <TableCellDateTime data={data} />,
            },

            {
                field: "assetName",
                headerName: "Asset Name",
                flex: CellSize.xlarge,
                minWidth: CellSize.xlarge,
                renderCell: (data: any) => (
                    <TableCellLongTypography color={"text.secondary"} fontWeight={300} data={data.value} />
                ),
            },
        ];
        if (UserHasAcl(AclTypes.aclSuperUser))
            result.push({
                field: "key",
                headerName: "Key",
                flex: CellSize.small,
                minWidth: CellSize.small,
                renderCell: (data: any) => <TableCellTypography color={"text.secondary"} fontWeight={500} data={data.value} />,
            });

        result.push({
            field: "assetId",
            headerName: "Asset Id",
            flex: CellSize.xxsmall,
            minWidth: CellSize.xxsmall,
            renderCell: (data: any) => (
                <Link href={`${routePaths.asset.path}/${data.value}`} target={"_blank"} align="right" underline="hover">
                    {data.value}
                </Link>
            ),
        });

        return result;
    }, []);

    const handleResults = (
        result: DbDeliveryWatermarkNexguardInfos[] | null,
        success: boolean | null,
        activeTab: 0 | 1 | 2,
        message: any
    ) => {
        switch (activeTab) {
            case 0:
                setLastWatermarks(result ?? []);
                break;
            case 1:
                setWatermarks(result ?? []);
                break;
            case 2:
                setWatermark(result ? result[0] ?? undefined : undefined);
                result && result[0] && showPanelOrder(result[0].orderItemId);
        }
        setSearchState(
            success ? (result && result.length > 0 ? SearchState.result : SearchState.noResult) : SearchState.error
        );
        const error = success ? "" : message.message ?? message ?? "";
        setErrors((x) =>
            activeTab === 0 ? [error, x[1], x[2]] : activeTab === 1 ? [x[0], error, x[2]] : [x[0], x[1], error]
        );
        didSearchByTab.current[activeTab] = true;
    };

    const startRef = useRef<any>(undefined);
    const endRef = useRef<any>(undefined);

    const fetchLastN = (lastN: number, lastWatermarks) => {
        if (lastN !== lastWatermarks.length) {
            setSearchState(SearchState.loading);
            orderService
                .GetWatermarks(1, lastN)
                .then((response) => {
                    handleResults(response.watermarks, response.success, 0, response.message);
                })
                .catch((err) => handleResults(null, false, 0, err.message ?? err));
        }
    };

    const fetchRange = (dateValue: any) => {
        setDateChange(false);
        const start = dateValue[0]?.isValid()
            ? moment(new Date(dateValue[0]?.toString() || "")).format("YYYY-MM-DD")
            : null;
        const end = dateValue[1]?.isValid() ? moment(new Date(dateValue[1]?.toString() || "")).format("YYYY-MM-DD") : null;
        if ((start !== startRef.current || end !== endRef.current) && start && end) {
            setSearchState(SearchState.loading);
            orderService
                .GetWatermarksByDate(start, end)
                .then((response) => {
                    handleResults(response.watermarks, response.success, 1, response.message);
                    startRef.current = start;
                    endRef.current = end;
                })
                .catch((err) => handleResults(null, false, 1, err.message ?? err));
        }
    };

    const fetchWatermark = (searchByKey: number | undefined, watermark) => {
        if (searchByKey && watermark?.id != searchByKey) {
            setSearchState(SearchState.loading);
            orderService
                .GetWatermarkByKey(searchByKey)
                .then((response) => {
                    handleResults(response.watermark ? [response.watermark] : null, response.success, 2, response.message);
                })
                .catch((err) => handleResults(null, false, 2, err.message ?? err));
        }
    };

    const handleDateChange = useCallback(
        (newValue: DateRange<dayjs.Dayjs>) => {
            //   const newValueParsed: DateRange<dayjs.Dayjs> = newValue[0] && newValue[1] ? [
            //     newValue[0],
            //     newValue[0].add(12, "months").isBefore(newValue[1]) ? newValue[0].add(12, "months") : newValue[1],
            //   ] : null;

            setDateValue(newValue);
            setDateChange(true);
            cacheService.SetPageFilterByKey(location.pathname, "date", newValue);
        },
        [watermarks?.length, location]
    );
    const handleSearchByKeyChange = (e) => {
        setSearchByKey(e.target.value);
    };
    const handleClearSearchByKey = () => {
        setSearchByKey(0);
    };

    const handleLastNChange = useCallback(
        (e) => {
            const limit = 1000;
            const newValue = Math.min(e.target.value, limit);
            setLastN(newValue);
        },
        [lastWatermarks?.length]
    );

    const handleTabChange = useCallback(
        (e, tab) => {
            setActiveTab(tab);
            hidePanelOrder();
            switch (tab) {
                case 0:
                    setSearchState(
                        errors[tab] !== ""
                            ? SearchState.error
                            : lastWatermarks.length > 0
                                ? SearchState.result
                                : didSearchByTab.current[0]
                                    ? SearchState.noResult
                                    : SearchState.waitingInput
                    );
                    break;
                case 1:
                    setSearchState(
                        errors[tab] !== ""
                            ? SearchState.error
                            : watermarks.length > 0
                                ? SearchState.result
                                : didSearchByTab.current[1]
                                    ? SearchState.noResult
                                    : SearchState.waitingInput
                    );
                    break;
                case 2:
                    setSearchState(
                        errors[tab] !== ""
                            ? SearchState.error
                            : watermark
                                ? SearchState.result
                                : didSearchByTab.current[2]
                                    ? SearchState.noResult
                                    : SearchState.waitingInput
                    );
                    watermark && showPanelOrder(watermark.orderItemId);
                    break;
                default:
                    break;
            }
        },
        [watermark?.id, watermarks.length, lastWatermarks.length, errors, hidePanelOrder, setActiveTab, setSearchState]
    );

    const handleClickSearchByKey = useCallback(() => {
        fetchWatermark(searchByKey, watermark);
    }, [fetchWatermark, searchByKey, watermark?.id]);

    const handleClickLastNSearch = useCallback(() => {
        fetchLastN(lastN, lastWatermarks.length);
    }, [fetchLastN, lastWatermarks, lastN]);

    const handleClickRangeSearch = useCallback(() => {
        fetchRange(dateValue);
    }, [fetchRange, dateValue]);

    const handleFilterResultsChange = (e) => {
        setFilterResults(e.target.value);
    };

    const handleShortcut = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                if (activeTab === 0) fetchLastN(lastN, lastWatermarks);
                if (activeTab === 1) fetchRange(dateValue);
                if (activeTab === 2) fetchWatermark(searchByKey, watermark);
            }
        },
        [searchByKey, watermark?.id, activeTab, dateValue, lastN, lastWatermarks.length]
    );

    const handleSearch = useCallback(() => {
        switch (activeTab) {
            case 0:
                handleClickLastNSearch();
                break;
            case 1:
                handleClickRangeSearch();
                break;
            case 2:
                handleClickSearchByKey();
                break;
        }
    }, [activeTab, handleClickLastNSearch, handleClickRangeSearch, handleClearSearchByKey]);

    useEventListener("keydown", handleShortcut);

    return (
        <>
            <ListPageHeader spacing={0} padding={"0px !important"}>
                <ListPageHeaderGroupActions spacing={3} marginTop={1.5}>
                    <SecurityOutlinedIcon color="primary" />
                    <Divider sx={{ height: "32px" }} variant="middle" orientation="vertical" />
                    <Stack spacing={2} direction={"row"}>
                        {activeTab === 0 && (
                            <Stack alignItems={"center"} spacing={0.5} direction={"row"}>
                                <Typography>Last</Typography>
                                <OutlinedInput
                                    disabled={searchState === SearchState.loading}
                                    value={lastN}
                                    onChange={handleLastNChange}
                                    sx={{ width: "64px" }}
                                    type="number"
                                />
                                <Typography>Watermarks</Typography>
                            </Stack>
                        )}
                        {activeTab === 1 && (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateRangePicker
                                    disabled={searchState === SearchState.loading}
                                    slotProps={{
                                        shortcuts: {
                                            items: shortcutsItems,
                                        },
                                        textField: { size: "small" },
                                    }}
                                    closeOnSelect={false}
                                    displayWeekNumber={true}
                                    fixedWeekNumber={5}
                                    maxDate={dateValue[0]?.add(12, "months")}
                                    showDaysOutsideCurrentMonth={true}
                                    slots={{ field: SingleInputDateRangeField, clearIcon: DateRangeOutlinedIcon }}
                                    sx={{
                                        position: "relative",
                                        ".MuiOutlinedInput-root": {
                                            marginTop: "0px !important",
                                        },
                                    }}
                                    value={dateValue}
                                    onChange={handleDateChange}
                                    format="DD/MM/YYYY"
                                />
                            </LocalizationProvider>
                        )}
                        {activeTab === 2 && (
                            <OutlinedInput
                                placeholder="Search by Key..."
                                onChange={handleSearchByKeyChange}
                                value={searchByKey}
                                type="number"
                                endAdornment={
                                    <>
                                        {searchByKey !== undefined && searchByKey > 0 && (
                                            <IconButton aria-label="Clear field" onClick={handleClearSearchByKey} size="small">
                                                <CloseIcon />
                                            </IconButton>
                                        )}
                                    </>
                                }
                            />
                        )}
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            disabled={
                                (activeTab === 0 &&
                                    (!lastN || lastN == lastWatermarks.length || searchState === SearchState.loading)) ||
                                (activeTab === 1 &&
                                    (!dateValue[0]?.isValid() ||
                                        !dateValue[1]?.isValid() ||
                                        searchState === SearchState.loading ||
                                        !dateChange)) ||
                                (activeTab === 2 &&
                                    (!searchByKey || searchByKey == watermark?.id || searchState === SearchState.loading))
                            }
                            onClick={handleSearch}
                            endIcon={<SearchOutlinedIcon fontSize="small" color="primary" />}
                        >
                            Search
                        </Button>
                    </Stack>
                    {activeTab !== 2 && searchState === SearchState.result && (
                        <Divider sx={{ height: "32px" }} variant="middle" orientation="vertical" />
                    )}
                    {activeTab !== 2 && searchState === SearchState.result && (
                        <OutlinedInput
                            endAdornment={
                                <>
                                    {filterResults !== "" && (
                                        <IconButton size="small" onClick={() => setFilterResults("")}>
                                            <CloseIcon color="error" fontSize="small" />
                                        </IconButton>
                                    )}
                                    <FilterAltIcon fontSize="small" />
                                </>
                            }
                            placeholder="Filter results..."
                            onChange={handleFilterResultsChange}
                            value={filterResults}
                        />
                    )}
                </ListPageHeaderGroupActions>
                <ListPageHeaderGroupActions spacing={3} marginTop={1.5}></ListPageHeaderGroupActions>
                <ListPageHeaderRight>
                    <Tabs value={activeTab} onChange={handleTabChange}>
                        <Tab value={0} label={"last " + lastN} />
                        <Tab value={1} label={"Date Range"} />
                        <Tab value={2} label="Payload" />
                    </Tabs>
                </ListPageHeaderRight>
            </ListPageHeader>
            {searchState === SearchState.loading && <TableSkeleton />}
            {activeTab !== 2 && searchState === SearchState.result && (
                <DataTable
                    tableKey={TableUuids.ORDERS_WATERMARKS}
                    getRowHeight={() => "auto"}
                    onRowClick={(params: GridRowParams) => showPanelOrder(params?.row?.orderItemId)}
                    rows={(activeTab === 0 ? lastWatermarks : watermarks).filter(
                        (x) =>
                            filterResults === "" ||
                            x.assetName?.toLowerCase().includes(filterResults.toLowerCase()) ||
                            x.assetId?.toString().includes(filterResults) ||
                            x.orderItemId?.toString().includes(filterResults) ||
                            x.id?.toString().includes(filterResults) ||
                            x.key?.toLowerCase().includes(filterResults.toLowerCase())
                    )}
                    columns={columns}
                    initialState={{ pinnedColumns: { left: ["id"], right: ["assetId"] } }}
                />
            )}
            {activeTab === 2 && searchState === SearchState.result && watermark && (
                <Card sx={{ width: "100%" }}>
                    <CardHeader
                        title={`Watermark Id #${watermark?.id}`}
                        content={
                            <Stack direction={"row"}>
                                <Typography color={"text.secondary"}>Key</Typography>
                                <Typography color={"text.primary"} variant="body1">
                                    Watermark Id #{watermark?.id}
                                </Typography>
                            </Stack>
                        }
                    />
                    <CardContent>
                        <Stack direction={"column"} spacing={1}>
                            <SingleLabelValue
                                label="Asset Id"
                                value={watermark.assetId}
                                link={`${routePaths.asset.path}/${watermark.assetId}`}
                            />

                            <Divider sx={{ width: "100%" }} variant="middle" orientation="horizontal" />
                            <SingleLabelValue label="Asset Name" value={watermark.assetName} />
                            <Divider sx={{ width: "100%" }} variant="middle" orientation="horizontal" />
                            <SingleLabelValue label="Key" value={watermark.key} />
                            <Divider sx={{ width: "100%" }} variant="middle" orientation="horizontal" />
                            <SingleLabelValue label="Order Item Id" value={watermark.orderItemId} />
                            <Divider sx={{ width: "100%" }} variant="middle" orientation="horizontal" />
                            <SingleLabelValue label="Date" value={moment(watermark.date).format("YYYY/MM/DD hh:mm")} />
                        </Stack>
                    </CardContent>
                </Card>
            )}
            {searchState === SearchState.noResult && (
                <EmptyListSlot
                    title="No watermark found!"
                    desc="We don't have any result for your search"
                    icon={<CollectionsBookmarkOutlinedIcon />}
                    sx={{ background: "transparent" }}
                    height="100%"
                ></EmptyListSlot>
            )}
            {searchState === SearchState.waitingInput && (
                <EmptyListSlot
                    title="Start a search"
                    desc="Please type a payload Id to find related watermark"
                    icon={<SearchOutlinedIcon />}
                    sx={{ background: "transparent" }}
                    height="100%"
                ></EmptyListSlot>
            )}
            {searchState === SearchState.error && (
                <EmptyListSlot
                    title="Oops! Something went wrong"
                    desc={errors[activeTab]}
                    icon={<ErrorOutlineOutlinedIcon />}
                    sx={{ background: "transparent" }}
                    height="100%"
                ></EmptyListSlot>
            )}
            <OrderItemPanel open={openPanelOrderItem} onClose={hidePanelOrder} itemId={selectedOrderItemId} />
        </>
    );
}

export default OrderWatermarks;
